import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, X } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { useUser } from '@/contexts/UserContext';
import Loading from '@/components/Loading';
import { useHeader } from '@/contexts/HeaderContext';
import { trackTrialStarted } from '@/lib/analytics';
import { useInAppPurchase } from '@/hooks/useInAppPurchase';
import { useStripePrices } from '@/hooks/useStripePrices';

const ANDROID_PACKAGE = 'com.greathusband.app';
const PLAY_SUBSCRIPTIONS_URL = `https://play.google.com/store/account/subscriptions?package=${ANDROID_PACKAGE}`;
const APPLE_SUBSCRIPTIONS_URL = 'https://apps.apple.com/account/subscriptions';

// Open the platform-native subscription management page. On iOS the
// itms-apps:// scheme deep-links straight into Settings → Apple ID →
// Subscriptions; on Android the play.google.com URL launches the Play
// Store app directly via Android intent handling.
const openStoreSubscriptionPage = async () => {
  const platform = Capacitor.getPlatform();
  if (platform === 'ios') {
    // itms-apps:// must go through window.location so iOS hands it off to
    // the App Store / Settings app instead of opening it in-app.
    window.location.href = 'itms-apps://apps.apple.com/account/subscriptions';
    return;
  }
  if (platform === 'android') {
    // Android resolves play.google.com URLs to the Play Store app.
    window.location.href = PLAY_SUBSCRIPTIONS_URL;
    return;
  }
  // Web fallback (or testing in browser): open the public store URL in a
  // new tab via Capacitor Browser if available, else window.open.
  try {
    await Browser.open({ url: PLAY_SUBSCRIPTIONS_URL });
  } catch (_) {
    window.open(PLAY_SUBSCRIPTIONS_URL, '_blank', 'noopener,noreferrer');
  }
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Hard ceiling on how long this screen may show its spinner, whatever the
// user context is doing. UserContext gives up on its own profile fetch after
// 8s, so this only ever fires if that mechanism itself failed.
const LOADING_FAILSAFE_MS = 10000;

const Subscription = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: contextUser, isPremium: contextIsPremium, loading: userLoading, fetchUserData } = useUser();
  // Fall back to the id persisted at login. /users/me can resolve with no
  // profile (offline, failed 401 recovery, or UserContext's own 8s safety
  // timeout) even though this device is signed in; without the fallback the
  // screen would have no id to load IAP products, the subscription status or
  // Restore Purchases with. Same pattern OnboardingV2 uses for its paywall.
  const userId = useMemo(() => {
    const fromContext = contextUser?.user_id || contextUser?.id;
    if (fromContext) return fromContext;
    try {
      return localStorage.getItem('betterhusbandUserId') || null;
    } catch (_e) {
      return null;
    }
  }, [contextUser]);
  const [loading, setLoading] = useState(true);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(null);
  const [user, setUser] = useState(null);
  const [contextPremium, setContextPremium] = useState(false);
  // Entitlement as reported by /subscription/status — see the effect below.
  const [statusIsPremium, setStatusIsPremium] = useState(false);
  // Lifetime owners have nothing to renew, cancel or manage, so the whole
  // management section is replaced with a plain statement of ownership.
  const [isLifetime, setIsLifetime] = useState(false);
  // Either source saying "active" is enough; neither one alone is reliable
  // during the seconds after a purchase or when /users/me is failing.
  const isPremium = contextPremium || statusIsPremium;
  const { isNative: isIAP, products: iapProducts, purchasing: iapPurchasing, error: iapError, purchase: iapPurchase, restorePurchases: iapRestore, loading: iapLoading, PRODUCT_IDS } = useInAppPurchase(userId);
  const stripePrices = useStripePrices(['it', 'es'].includes(i18n.language?.substring(0, 2)) ? 'eur' : 'usd');

  // Surface IAP errors as toasts
  useEffect(() => {
    if (iapError) toast.error(iapError);
  }, [iapError]);

  // Any user without an active plan (free / expired / cancelled / null) who
  // lands on /subscription was trying to subscribe — route them straight to
  // the final paywall step instead of showing the management screen. This
  // centralizes the rule so every "Subscribe" CTA across the app (Dashboard,
  // SlideMenu, NotificationBell, Achievements, Stats, Settings, …) ends up
  // at the same place without each call site needing its own check.
  useEffect(() => {
    if (userLoading) return;
    if (!contextUser) return;
    if (contextIsPremium) return;
    const status = contextUser?.subscription_status;
    if (status === 'premium' || status === 'trial') return;
    navigate('/onboarding?step=paywall&paywall_step=24', { replace: true });
  }, [userLoading, contextUser, contextIsPremium, navigate]);

  const { setHeaderProps } = useHeader();
  useEffect(() => {
    setHeaderProps({ title: t('subscription.title') });
  }, [t, setHeaderProps]);

  // Handle return from Stripe portal
  useEffect(() => {
    const fromStripe = searchParams.get('from');
    if (fromStripe === 'stripe') {
      // Remove the query parameter
      setSearchParams({});
      // Refresh user data to get any updates from Stripe
      if (fetchUserData) {
        fetchUserData();
      }
      toast.success(t('subscription.returnedFromPortal'));
    }
  }, [searchParams, setSearchParams, fetchUserData, t]);

  // Resolve this screen's own loading state as soon as the user context has
  // settled — regardless of whether a profile actually came back.
  //
  // App Store Guideline 2.1 / review rejection: the old guard was
  // `!userLoading && userId`, so whenever UserContext finished with
  // `user === null` (failed /users/me, unrecovered 401, or its 8s safety
  // timeout) this flag stayed `true` and the page spun forever behind the
  // header — exactly what App Review saw on iPad. Every section below already
  // tolerates a null user, so render the screen and let the status fetch and
  // the retry below fill in the details.
  useEffect(() => {
    if (userLoading) return;
    if (contextUser) {
      setUser(contextUser);
      setContextPremium(contextIsPremium);
    }
    setLoading(false);
  }, [userLoading, contextUser, contextIsPremium]);

  // Absolute backstop: nothing may keep this screen on a spinner. If the user
  // context never settles at all (hung request, remounted provider), show the
  // page anyway so the subscribe / restore paths stay reachable. Sits just
  // past UserContext's own 8s safety timeout, so in practice the effect above
  // has already fired and this never runs.
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setLoading((prev) => {
        if (prev) console.warn('[Subscription] Loading failsafe fired — rendering without user context');
        return false;
      });
    }, LOADING_FAILSAFE_MS);
    return () => clearTimeout(timeoutId);
  }, []);

  // The context settled without a profile — take exactly one shot at
  // recovering it so a transient failure heals itself without the user having
  // to relaunch the app. Guarded by a ref: fetchUserData flips `userLoading`,
  // which would otherwise re-trigger this effect in a loop.
  const profileRetriedRef = useRef(false);
  useEffect(() => {
    if (userLoading || contextUser || profileRetriedRef.current) return;
    if (typeof fetchUserData !== 'function') return;
    profileRetriedRef.current = true;
    Promise.resolve(fetchUserData()).catch(() => { /* non-fatal — page renders regardless */ });
  }, [userLoading, contextUser, fetchUserData]);

  // Always fetch the freshest subscription dates (purchase / next billing /
  // trial end) from the dedicated endpoint. The user-context object is
  // cached and may not reflect a webhook update yet.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/subscription/status/${userId}`);
        if (cancelled || !data) return;
        setUser((prev) => ({
          ...(prev || {}),
          subscription_status: data.subscription_status ?? prev?.subscription_status,
          trial_ends_at: data.trial_ends_at ?? prev?.trial_ends_at,
          current_period_end: data.current_period_end ?? prev?.current_period_end,
        }));
        // This endpoint is the authority on entitlement, so it can also stand
        // in for the user context when that failed to load — otherwise an
        // active subscriber with a broken /users/me would be shown the
        // "subscribe" pitch for a plan they already pay for. Kept in its own
        // state rather than folded into `isPremium`: the context effect above
        // re-runs on every contextUser identity change and would otherwise
        // stomp this back to a stale `false` right after a purchase.
        setStatusIsPremium(
          data.subscription_status === 'premium' || data.subscription_status === 'trial'
        );
        setIsLifetime(Boolean(data.is_lifetime) || data.plan_type === 'lifetime');
        // Never leave the spinner up once we know the subscription state.
        setLoading(false);
      } catch (e) {
        // Non-fatal — UI will fall back to cached context fields.
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const handleUpgrade = async () => {
    let handedOffToSuccess = false;
    const handOffToSuccess = () => {
      if (handedOffToSuccess) return;
      handedOffToSuccess = true;
      navigate('/premium/success?source=iap&return=/dashboard', { replace: true });
    };

    try {
      setUpgradeLoading(true);

      // On native iOS/Android, use In-App Purchase (only supported channel).
      if (isIAP) {
        // Pass the id explicitly (same as OnboardingV2) so the store's
        // applicationUsername is set even when the user context failed to load.
        const result = await iapPurchase(PRODUCT_IDS.LIFETIME, { userId, onApprovalQueued: handOffToSuccess });
        if (handedOffToSuccess) return;
        if (result === false) {
          // Purchase explicitly cancelled / no offer returned. Stay silent.
          setUpgradeLoading(false);
          return;
        }
        if (result !== true) {
          console.error('IAP purchase did not return verified success:', result);
          toast.error(t('premium.errorCheckout'));
          setUpgradeLoading(false);
          return;
        }

        handOffToSuccess();
        setUpgradeLoading(false);
        return;
      }

      // Web: Stripe is intentionally disabled. Direct users to the mobile app.
      toast.info(t('subscription.webNotAvailable'));
      setUpgradeLoading(false);
    } catch (error) {
      if (handedOffToSuccess) return;
      console.error('Error starting purchase:', error);
      toast.error(t('premium.errorCheckout'));
      setUpgradeLoading(false);
    }
  };

  // Web (Stripe) subscribers manage everything through the Stripe customer
  // portal — including cancelling during the trial.
  const handleOpenPortal = async () => {
    if (!userId) return;
    setPortalLoading('portal');
    try {
      const { data } = await axios.post(`${API}/subscription/customer-portal`, {
        user_id: userId,
        origin_url: window.location.origin,
      });
      if (data?.portal_url) {
        window.location.href = data.portal_url;
        return;
      }
      throw new Error('no portal URL');
    } catch (error) {
      console.error('Error opening customer portal:', error);
      const detail = error?.response?.data?.detail || '';
      // No Stripe customer means they subscribed via an app store
      toast.error(detail.includes('No Stripe customer')
        ? t('subscription.storeManagedDescWeb')
        : t('premium.errorCheckout'));
      setPortalLoading(null);
    }
  };

  const handleDismiss = () => {
    navigate('/dashboard', { replace: true });
  };

  // Section Component
  const Section = ({ title, children }) => (
    <div className="mb-6">
      <div className="px-6">
        <h2 className="text-xs font-semibold text-stone-400 dark:text-[rgba(255,255,255,0.5)] uppercase tracking-wider mb-2">
          {title}
        </h2>
      </div>
      <div className="bg-white dark:bg-[rgba(255,255,255,0.07)] rounded-xl border border-stone-200 dark:border-[rgba(255,255,255,0.08)] divide-y divide-stone-200 dark:divide-[rgba(255,255,255,0.06)]">
        <div className="px-4">
          {children}
        </div>
      </div>
    </div>
  );

  // Setting Row Component
  const SettingRow = ({ title, description, children, onClick, showArrow }) => (
    <button
      onClick={onClick}
      disabled={!onClick || portalLoading !== null}
      className={`w-full flex items-center justify-between py-3 ${onClick ? 'hover:bg-stone-50 dark:hover:bg-[rgba(255,255,255,0.05)] -mx-4 px-4 rounded-full cursor-pointer' : ''} ${portalLoading !== null ? 'opacity-50' : ''}`}
    >
      <div className="text-left flex-1 min-w-0">
        <p className="text-[16px] font-medium text-stone-900 dark:text-white">{title}</p>
        {description && (
          <p className="text-xs text-stone-500 dark:text-[rgba(255,255,255,0.5)] mt-0.5">{description}</p>
        )}
      </div>
      <div className="flex items-center gap-2 ml-4">
        {children}
        {showArrow && <ChevronRight className="w-4 h-4 text-stone-400 dark:text-[rgba(255,255,255,0.4)]" />}
      </div>
    </button>
  );

  // One product, one price — live from the store on native, live from Stripe
  // on web. No hardcoded fallback: an unknown price shows a placeholder and
  // keeps the buy button disabled rather than quoting a number we can't back.
  const currentLang = i18n.language?.substring(0, 2) || 'en';
  const currencySymbol = currentLang === 'it' ? '€' : currentLang === 'es' ? '€' : '$';
  const lifetimeIapProduct = iapProducts?.find(p => p.period === 'lifetime');
  const webLifetimePrice = stripePrices.lifetime != null
    ? `${currencySymbol}${stripePrices.lifetime.toFixed(2)}`
    : null;
  const resolvedPrice = isIAP ? (lifetimeIapProduct?.price || null) : webLifetimePrice;
  const priceReady = Boolean(resolvedPrice);
  const premiumPrice = resolvedPrice || '…';
  const premiumPriceLabel = priceReady
    ? `${premiumPrice} · ${t('premium.lifetimeOneTime')}`
    : t('premium.lifetimeOneTime');

  const features = [
    t('subscription.feature1Premium'),
    t('subscription.feature2Premium'),
    t('subscription.feature3Premium'),
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center page-container" style={{ background: 'inherit' }}>
        <Loading size="hundred" message={null} />
      </div>
    );
  }

  return (
    <div style={{ background: 'inherit', display: 'flex', justifyContent: 'center' }}>
      <div style={{ width: '100%', maxWidth: 'clamp(390px, 70vw, 560px)', margin: '0 auto', minHeight: 844, background: 'inherit', position: 'relative', paddingBottom: 80, overflowY: 'auto' }}>
        <div style={{ padding: '24px 24px 0', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label={t('subscription.dismissRequiredAccess')}
            data-testid="subscription-dismiss-btn"
            style={{
              minHeight: 44,
              minWidth: 44,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              padding: '10px 14px',
              background: '#ffffff',
              color: '#111827',
              border: '1px solid rgba(17,24,39,0.14)',
              borderRadius: 12,
              cursor: 'pointer',
              boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
              fontFamily: 'Manrope-Variable, serif',
              fontSize: 15,
              fontWeight: 700,
            }}
          >
            <X aria-hidden="true" style={{ width: 18, height: 18, strokeWidth: 2.4 }} />
            <span>{t('subscription.dismiss')}</span>
          </button>
        </div>

        <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '12px 24px' }} />

        <div style={{ padding: '0 24px' }}>
          {/* Current Plan Status */}
          <Section title={t('subscription.currentPlan')}>
            <SettingRow
              title={isPremium
                ? (isLifetime ? t('premium.lifetimeLabel') : t('subscription.premium'))
                : t('subscription.noSubscription')}
              description={isPremium
                // Owners see what they have, not what it currently costs.
                ? (isLifetime ? t('premium.lifetimeTagline') : premiumPriceLabel)
                : t('subscription.noSubscriptionDesc')
              }
            >
              <div className={`flex items-center gap-2 px-3 py-1 rounded-none text-xs font-medium ${
                isPremium 
                  ? 'bg-stone-900 text-white'
                  : 'bg-stone-100 dark:bg-[rgba(255,255,255,0.1)] text-stone-600 dark:text-[rgba(255,255,255,0.5)]'
              }`}>
                {isPremium ? t('subscription.premium') : t('subscription.inactive')}
              </div>
            </SettingRow>
            {(() => {
              if (!user) return null;
              // Lifetime has no renewal or trial date to show.
              if (isLifetime) return null;
              const status = user.subscription_status;
              const isTrial = status === 'trial';
              const dateStr = isTrial
                ? (user.trial_ends_at || user.current_period_end)
                : (user.current_period_end || user.trial_ends_at);
              if (!dateStr) return null;
              const d = new Date(dateStr);
              if (isNaN(d.getTime())) return null;
              const locale = i18n.language?.startsWith('it') ? 'it-IT' : i18n.language?.startsWith('es') ? 'es-ES' : 'en-US';
              const formatted = d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
              const label = isTrial ? t('subscription.trialEndsOn') : t('subscription.nextBilling');
              return (
                <div style={{ padding: '8px 0 4px', display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span className="text-stone-600 dark:text-[rgba(255,255,255,0.6)]">{label}</span>
                  <span className="text-stone-900 dark:text-white font-medium">{formatted}</span>
                </div>
              );
            })()}
          </Section>

          {/* Premium Features */}
          {!isPremium && (
            <Section title={t('subscription.subscribe')}>
              <div className="py-4 space-y-3">
                {/* Billed amount first and largest — App Store Guideline 3.1.2(c)
                    requires the charged amount to be the most conspicuous pricing
                    element; the one-time terms follow in subordinate microcopy. */}
                <div className="text-center mb-2">
                  <p className="text-xs font-medium text-stone-500 dark:text-[rgba(255,255,255,0.5)] leading-none">
                    {t('premium.lifetimeOneTime')}
                  </p>
                  <p data-testid="billed-amount" className="text-3xl font-extrabold text-stone-900 dark:text-white leading-none mt-1.5 tracking-tight">
                    {premiumPrice}
                  </p>
                  <p className="text-xs text-stone-500 dark:text-[rgba(255,255,255,0.5)] mt-1">
                    {t('premium.lifetimeTagline')}
                  </p>
                </div>
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <span className="text-amber-500 text-xs mt-0.5">✦</span>
                    <p className="text-sm text-stone-700 dark:text-[rgba(255,255,255,0.7)] flex-1">{feature}</p>
                  </div>
                ))}
                
                <button
                  onClick={handleUpgrade}
                  disabled={upgradeLoading || iapPurchasing || (isIAP && iapLoading) || !priceReady}
                  className="w-full mt-4 h-11 rounded-none font-medium transition-all bg-stone-900 text-white disabled:opacity-50 flex items-center justify-center gap-2 active:scale-95"
                >
                  {(upgradeLoading || iapPurchasing) ? (
                    <img src="/res/pianta_cresce_fill.gif" alt="Caricamento" className="w-5 h-5" />
                  ) : (
                    t('premium.upgradeNow')
                  )}
                </button>
                <p className="text-[11px] text-center text-stone-500 dark:text-[rgba(255,255,255,0.4)] mt-2 leading-snug">
                  {t('premium.billingTerms', { price: premiumPrice })}
                </p>
              </div>
            </Section>
          )}

          {/* Lifetime owners: nothing to manage. Showing a "manage / cancel
              subscription" row to someone who made a one-time purchase would
              send them to a store page with nothing on it. */}
          {isPremium && isLifetime && (
            <Section title={t('subscription.management')}>
              <div className="py-3">
                <p className="text-sm font-medium text-stone-700 dark:text-white mb-1">
                  {t('premium.lifetimeOwned')}
                </p>
                <p className="text-xs text-stone-600 dark:text-[rgba(255,255,255,0.5)] leading-relaxed">
                  {t('premium.lifetimeOwnedDesc')}
                </p>
              </div>
            </Section>
          )}

          {/* Manage Subscription (legacy subscribers) — store-managed only */}
          {isPremium && !isLifetime && (
            <Section title={t('subscription.management')}>
              <div className="py-2">
                <p className="text-sm font-medium text-stone-700 dark:text-white mb-1">
                  {t('subscription.manualPremiumTitle')}
                </p>
                <p className="text-xs text-stone-600 dark:text-[rgba(255,255,255,0.5)] leading-relaxed mb-3">
                  {Capacitor.getPlatform() === 'ios'
                    ? t('subscription.storeManagedDescApple')
                    : Capacitor.getPlatform() === 'android'
                      ? t('subscription.storeManagedDescGoogle')
                      : t('subscription.storeManagedDescWeb')}
                </p>
              </div>
              {(Capacitor.getPlatform() === 'ios' || Capacitor.getPlatform() === 'android') ? (
                <SettingRow
                  title={t(
                    Capacitor.getPlatform() === 'ios'
                      ? 'subscription.manageOnApple'
                      : 'subscription.manageOnGoogle'
                  )}
                  description={t(
                    Capacitor.getPlatform() === 'ios'
                      ? 'subscription.manageOnAppleDesc'
                      : 'subscription.manageOnGoogleDesc'
                  )}
                  onClick={openStoreSubscriptionPage}
                  showArrow
                />
              ) : (
                <SettingRow
                  title={t('subscription.manageOnStripe')}
                  description={t('subscription.manageOnStripeDesc')}
                  onClick={handleOpenPortal}
                  showArrow
                />
              )}
            </Section>
          )}

          {/* Info Section */}
          {isIAP && (
            <div className="mt-4">
              <button
                onClick={async () => {
                  toast.info(i18n.language === 'it' ? 'Ripristino acquisti...' : i18n.language === 'es' ? 'Restaurando compras...' : 'Restoring purchases...');
                  await iapRestore();
                  await fetchUserData();
                  toast.success(i18n.language === 'it' ? 'Acquisti ripristinati' : i18n.language === 'es' ? 'Compras restauradas' : 'Purchases restored');
                }}
                disabled={iapLoading}
                className="w-full h-11 rounded-xl font-medium transition-all bg-transparent border border-stone-300 dark:border-[rgba(255,255,255,0.15)] text-stone-700 dark:text-white disabled:opacity-50 flex items-center justify-center gap-2 active:scale-95"
              >
                {iapLoading
                  ? (i18n.language === 'it' ? 'Ripristino...' : i18n.language === 'es' ? 'Restaurando...' : 'Restoring...')
                  : (i18n.language === 'it' ? 'Ripristina acquisti' : i18n.language === 'es' ? 'Restaurar compras' : 'Restore Purchases')}
              </button>
            </div>
          )}
          <div className="mt-6 bg-stone-50 dark:bg-[rgba(255,255,255,0.07)] rounded-xl p-4 border border-stone-200 dark:border-[rgba(255,255,255,0.08)]">
            <div className="flex gap-3">
              <div className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs font-medium text-stone-700 dark:text-white mb-1">
                  {t('subscription.infoTitle') || 'Informazioni'}
                </p>
                <p className="text-xs text-stone-600 dark:text-[rgba(255,255,255,0.5)] leading-relaxed">
                  {isPremium
                    ? (isLifetime
                      ? t('premium.lifetimeOwnedDesc')
                      : (t('subscription.infoPremium') || 'Il tuo abbonamento si rinnova automaticamente. Puoi annullarlo in qualsiasi momento.'))
                    : (t('subscription.infoFree') || 'Passa a Premium per sbloccare tutte le funzionalità e migliorare la tua relazione.')
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscription;
